import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import theme from '@ep/theme';
import { Gcms_Post } from '@ep/types/gatsbyTypes';
import useScroll from '@ep/utils/useScroll';
import { clear } from 'console';
import React, { useEffect, useRef, useState } from 'react';
import Fade from 'react-reveal';
import CuratedPost from './CuratedPost';
import Post from './Post';

export interface PostFeatureModuleProps {
  label?: string;
  posts?: Gcms_Post[];
  featuredPosts: Gcms_Post;
  curatedPosts: Gcms_Post;
  backgroundColor?: string;
  boxColor?: string;
}

export interface BoxProps {
  boxColor: string | undefined;
}

const Grid = styled.div`
  display: grid;
  grid-gap: 1rem 0px;
  grid-template-columns: auto auto;
  @media (max-width: ${theme.breakpoints.optimalMobile + 700}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StyledContainer = styled(Container)`
  margin-left: 1rem;
  margin-right: 2rem;
  max-width: 1400px;
`;

const PostDiv = styled.div<BoxProps>`
  max-width: 100%;
  ${({ boxColor }) => `
    background-color: ${boxColor ? boxColor : `white`};
  `}
  display: flex;
  position: relative;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const PageIndicators = styled.div<BoxProps>`
  ${({ boxColor }) => `
    background-color: ${boxColor ? boxColor : `white`};
  `}
  display: flex;
  justify-content: center;
`;

interface PageIndicatorProps {
  active: boolean;
  themeColor?: string;
}

const PageIndicator = styled.div<PageIndicatorProps>`
  margin: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: transparent;
  transition: ${theme.transitions.create(['background-color', 'border-color'])};
  cursor: pointer;

  ${({ themeColor }) =>
    `
    border: 1px solid ${
      themeColor && themeColor != theme.colors.white
        ? theme.colors.white
        : theme.colors.mediumBlue
    };
  `}

  ${({ active, themeColor }) =>
    active
      ? `
        background-color: ${
          themeColor && themeColor != theme.colors.white
            ? theme.colors.white
            : theme.colors.mediumBlue
        };

      `
      : `
        background-color: ${themeColor}
      `}
`;

const LeftContainer = styled.div<BoxProps>`
  ${({ boxColor }) => `
    background-color: ${boxColor ? boxColor : `white`};
  `}
  display:flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 100%;
  @media (max-width: ${theme.breakpoints.optimalTablet + 400}px) {
    min-height: 30rem;
  }
`;

const CuratedPostsContainer = styled.div<BoxProps>`
  ${({ boxColor }) => `
    background-color: ${boxColor ? boxColor : `white`};
  `}
  margin-left: 1rem;
  padding-top: 0.5rem;
  padding-right: 1rem;
  width: 100%;
  @media (max-width: ${theme.breakpoints.optimalMobile + 700}px) {
    margin-left: 0rem;
  }
`;

const CuratedPosts = styled.div`
  text-align: left;
  margin-bottom: 1rem;
  flex: 3;
  padding-bottom: 1rem;
  h3 {
    font-size: 1rem;
  }
`;

function PostFeatureModule({
  featuredPosts,
  curatedPosts,
  backgroundColor,
  boxColor,
}: PostFeatureModuleProps) {
  const postsRef = useRef<HTMLDivElement>(null);
  const { x } = useScroll(postsRef);
  const currentIndex = postsRef.current
    ? Math.round(x / postsRef.current.clientWidth)
    : 0;

  const [autoScroll, setAutoScroll] = useState(false);

  const runScrolling = () => {
    setAutoScroll(true);
  };

  function scrollTo(index: number) {
    postsRef.current?.scroll({
      left: (postsRef.current.children[index] as HTMLElement).offsetLeft,
      behavior: 'smooth',
    });
  }
  const [autoIndex, setAutoIndex] = useState(0);
  const [count, setCount] = useState(0);
  let limitRun = featuredPosts.length * 2;

  useEffect(() => {
    if (count === 0 && autoScroll) {
      setAutoIndex(autoIndex + 1);
      setCount(count + 1);
      scrollTo(autoIndex);
    }
    if (count <= limitRun && autoScroll) {
      setTimeout(() => {
        if (autoIndex >= featuredPosts.length - 1) {
          setAutoIndex(0);
        } else {
          setAutoIndex(autoIndex + 1);
        }
        setCount(count + 1);
        scrollTo(autoIndex);
      }, 4000);
    }
  }, [count, autoScroll]);

  const chunkPosts = (arr, size) =>
    arr.reduce(
      (acc, e, i) => (
        i % size ? acc[acc.length - 1].push(e) : acc.push([e]), acc
      ),
      [],
    );

  const postChunked: Gcms_Post[] = chunkPosts(featuredPosts, 1);

  return (
    <StyledContainer backgroundColor={backgroundColor}>
      <Fade onReveal={() => runScrolling()} />
      <Grid>
        <LeftContainer boxColor={boxColor}>
          <PostDiv ref={postsRef} tabIndex={0} boxColor={boxColor}>
            {postChunked &&
              postChunked.map((post, index: number) => (
                <Post key={`Post__Chunked__Card__${index}`} post={post} />
              ))}
          </PostDiv>
          <PageIndicators boxColor={boxColor}>
            {featuredPosts &&
              featuredPosts.length > 1 &&
              featuredPosts.map((post, index) => (
                <PageIndicator
                  key={post.id}
                  active={currentIndex === index}
                  onClick={() => {
                    setAutoScroll(false);
                    scrollTo(index);
                  }}
                  themeColor={backgroundColor}
                />
              ))}
          </PageIndicators>
        </LeftContainer>
        <CuratedPostsContainer boxColor={boxColor}>
          <CuratedPosts>
            {curatedPosts.map(curatedPost => (
              <CuratedPost key={curatedPost.id} curatedPost={curatedPost} />
            ))}
          </CuratedPosts>
        </CuratedPostsContainer>
      </Grid>
    </StyledContainer>
  );
}

export default React.memo(PostFeatureModule);
